import { useCallback, useState } from "react";

import {
  ClientInfo,
  fetchClientInfoUtil,
  fetchClientNamesUtil,
} from "~/pages/purchase-orders/hooks/utils/clientUtils";
import { fetchProfilesUtil } from "~/pages/purchase-orders/hooks/utils/profileUtils";
import { fetchSalesRequestItemsUtil } from "~/pages/purchase-orders/hooks/utils/salesRequestItemsUtils";
import { ClientData } from "~/pages/purchase-orders/hooks/salesRequests/useFetchSalesRequests";
import { SalesRequest, SalesRequestItem } from "~/types/client";

export const useFetchRelatedData = (
  setSalesRequestItems: React.Dispatch<
    React.SetStateAction<Record<string, SalesRequestItem[]>>
  >
) => {

  const [clientNames, setClientNames] = useState<Record<string, string>>({});
  const [clientInfo, setClientInfo] = useState<Record<string, ClientInfo>>({});
  const [sellerProfiles, setSellerProfiles] = useState<Record<string, any>>({});
  const [approverProfiles, setApproverProfiles] = useState<Record<string, any>>(
    {}
  );

  const fetchClientNames = useCallback(
    async (requests: SalesRequest[], existingClientData?: ClientData) => {
      // If client data was pre-fetched in the initial query, use it directly
      // This eliminates N+1 queries for client data
      if (existingClientData) {
        setClientNames((prev) => ({ ...prev, ...existingClientData.names }));
        setClientInfo((prev) => ({ ...prev, ...existingClientData.info }));
        return;
      }

      // Fallback: fetch client data if not provided (backwards compatibility)
      const clientNameMap = await fetchClientNamesUtil(requests);
      let clientInfoMap: Record<string, ClientInfo> = {};
      try {
        clientInfoMap = await fetchClientInfoUtil(requests);
      } catch (error) {
        console.error("useFetchRelatedData - fetchClientInfoUtil error:", error);
        // Continue with empty clientInfoMap
      }

      setClientNames(clientNameMap);
      setClientInfo(clientInfoMap);

      // For backward compatibility, also populate clientNames with info from deleted clients
      const enhancedClientNames = { ...clientNameMap };
      Object.entries(clientInfoMap).forEach(([clientId, info]) => {
        if (!enhancedClientNames[clientId]) {
          enhancedClientNames[clientId] = info.name;
        }
      });
      setClientNames(enhancedClientNames);
    },
    []
  );

  const fetchSellerProfiles = useCallback(async (requests: SalesRequest[]) => {
    const sellerIds = [...new Set(requests.map((req) => req.createdBy))];
    const sellerProfileMap = await fetchProfilesUtil(sellerIds);
    setSellerProfiles(sellerProfileMap);
  }, []);

  const fetchApproverProfiles = useCallback(async (requests: SalesRequest[]) => {
    const approverIds = [
      ...new Set(
        requests
          .filter((req) => req.approvedRejectedBy)
          .map((req) => req.approvedRejectedBy as string)
      ),
    ];

    if (approverIds.length === 0) {
      return;
    }

    const approverProfileMap = await fetchProfilesUtil(approverIds);
    setApproverProfiles(approverProfileMap);
  }, []);

  const fetchSalesRequestItems = useCallback(async (requests: SalesRequest[]) => {
    const itemsMap = await fetchSalesRequestItemsUtil(requests);
    setSalesRequestItems(itemsMap);
  }, [setSalesRequestItems]);

  return {
    clientNames,
    clientInfo,
    sellerProfiles,
    approverProfiles,
    fetchClientNames,
    fetchSellerProfiles,
    fetchApproverProfiles,
    fetchSalesRequestItems,
  };
};
